import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Badge, Button, Card, EmptyState, ErrorNote, Field, Input, Modal, PageHeader, Select, Spinner, statusTone, Table, Textarea } from "../components/ui";
import { SENDING_PROVIDER_LABELS, STATUS_LABELS, fmtDateTime } from "../lib/format";

type SendingAccount = {
  id: string; name: string; email: string; domain: string; provider: string; smtpHost: string; smtpPort: number;
  smtpUsername: string | null; smtpPasswordEnc: string | null; groupName: string | null; dailySendingLimit: number;
  status: string; monitoringEnabled: boolean; connectionStatus: string; lastCheckedAt: string | null; notes: string | null;
  oauthEnabled: boolean; oauthClientId: string | null;
};

const EMPTY = { name: "", email: "", domain: "", smtpHost: "", smtpPort: 587, smtpUsername: "", smtpPassword: "", smtpEncryption: "starttls", provider: "custom_smtp", groupName: "", dailySendingLimit: 200, status: "inactive", monitoringEnabled: false, oauthEnabled: false, oauthClientId: "", notes: "" };

export function SendingAccounts() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<"create" | "import" | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<any>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["sending-accounts", search],
    queryFn: () => api<{ items: SendingAccount[] }>(`/sending-accounts?search=${encodeURIComponent(search)}`),
  });
  const { data: oauthClients } = useQuery({ queryKey: ["oauth-clients"], queryFn: () => api<any[]>("/oauth/clients") });

  const save = useMutation({
    mutationFn: (payload: any) =>
      editId ? api(`/sending-accounts/${editId}`, { method: "PATCH", body: JSON.stringify(payload) }) : api("/sending-accounts", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sending-accounts"] }); setModal(null); setForm(EMPTY); setEditId(null); },
    onError: setError,
  });

  const test = useMutation({
    mutationFn: (id: string) => api(`/sending-accounts/${id}/test-connection`, { method: "POST", body: "{}" }),
    onSuccess: (r: any) => { qc.invalidateQueries({ queryKey: ["sending-accounts"] }); alert(r.ok ? `SMTP OK: ${r.message}` : `SMTP failed: ${r.message}`); },
  });

  const del = useMutation({
    mutationFn: (id: string) => api(`/sending-accounts/${id}`, { method: "DELETE", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sending-accounts"] }),
  });

  const authorize = useMutation({
    mutationFn: async (accountId: string) => {
      const res = await api<{ url: string }>("/oauth/authorize", { method: "POST", body: JSON.stringify({ accountType: "sending", accountId }) });
      window.location.href = res.url;
    },
    onError: setError,
  });

  async function importCsv(file: File) {
    const fd = new FormData();
    fd.append("file", file);
    setResult(await api("/sending-accounts/import", { method: "POST", body: fd }));
    qc.invalidateQueries({ queryKey: ["sending-accounts"] });
  }

  return (
    <div>
      {(params.get("oauth") === "success" || params.get("oauth") === "error") && (
        <div className={params.get("oauth") === "success" ? "mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" : "mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"}>
          {params.get("oauth") === "success" ? "Microsoft account authorized — this sending account is now connected via OAuth." : `OAuth failed: ${params.get("message") ?? "unknown error"}`}
          <button className="ml-3 font-medium underline" onClick={() => setParams({})}>Dismiss</button>
        </div>
      )}
      <PageHeader
        title="Sending Accounts"
        subtitle="SMTP accounts whose placement and reputation are tested"
        actions={
          <>
            <Button variant="secondary" onClick={() => { setModal("import"); setResult(null); }}>CSV Import</Button>
            <Button onClick={() => { setForm(EMPTY); setEditId(null); setError(null); setModal("create"); }}>+ Add Sending Account</Button>
          </>
        }
      />
      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-3 border-b border-ink-100 p-4">
          <Input placeholder="Search email…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
          <a href="/api/v1/sending-accounts/import-template" className="text-xs font-medium text-brand-600 hover:text-brand-700">Download CSV template</a>
        </div>
        {isLoading ? <Spinner /> : !data?.items.length ? (
          <div className="p-6"><EmptyState title="No sending accounts" description="Add the SMTP accounts you want to test." /></div>
        ) : (
          <Table headers={["Name", "Email", "Domain", "Provider", "SMTP", "Status", "Monitoring", "Limit", "Actions"]}>
            {data!.items.map((a) => (
              <tr key={a.id} className="table-row-hover">
                <td className="px-4 py-3">
                  <Link to={`/accounts/sending/${a.id}`} className="font-medium text-ink-800 hover:text-brand-600">{a.name}</Link>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/accounts/sending/${a.id}`} className="text-ink-600 hover:text-brand-600">{a.email}</Link>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-500">{a.domain}</td>
                <td className="px-4 py-3"><Badge tone="neutral">{SENDING_PROVIDER_LABELS[a.provider] ?? a.provider}</Badge></td>
                <td className="px-4 py-3"><Badge tone={statusTone(a.connectionStatus)}>{a.connectionStatus}</Badge></td>
                <td className="px-4 py-3"><Badge tone={a.status === "active" ? "success" : "neutral"}>{STATUS_LABELS[a.status]}</Badge></td>
                <td className="px-4 py-3">{a.monitoringEnabled ? <Badge tone="brand">Monitored</Badge> : <Badge tone="neutral">Off</Badge>}</td>
                <td className="px-4 py-3 tabular-nums text-ink-500">{a.dailySendingLimit}/day</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button className="rounded px-2 py-1 text-xs text-brand-600 hover:bg-brand-50" onClick={() => { setForm({ ...EMPTY, ...a, smtpUsername: a.smtpUsername ?? "", groupName: a.groupName ?? "", notes: a.notes ?? "", oauthClientId: a.oauthClientId ?? "", smtpPassword: "" }); setEditId(a.id); setModal("create"); }}>Edit</button>
                    <button className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50" onClick={() => test.mutate(a.id)}>Test SMTP</button>
                    <Link to={`/accounts/sending/${a.id}?tab=history`} className="rounded px-2 py-1 text-xs text-ink-500 hover:bg-ink-50">History</Link>
                    <button className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50" onClick={() => { if (confirm(`Delete ${a.email}?`)) del.mutate(a.id); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Modal open={modal === "create"} onClose={() => setModal(null)} title={editId ? "Edit Sending Account" : "Add Sending Account"} wide>
        <div className="space-y-4">
          <ErrorNote error={error} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Sender Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" /></Field>
            <Field label="Domain"><Input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="example.com" /></Field>
            <Field label="Sending Provider">
              <Select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                {Object.entries(SENDING_PROVIDER_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </Select>
            </Field>
            <Field label="SMTP Host"><Input value={form.smtpHost} onChange={(e) => setForm({ ...form, smtpHost: e.target.value })} placeholder="smtp.example.com" /></Field>
            <Field label="SMTP Port"><Input type="number" value={form.smtpPort} onChange={(e) => setForm({ ...form, smtpPort: Number(e.target.value) })} /></Field>
            <Field label="SMTP Username"><Input value={form.smtpUsername} onChange={(e) => setForm({ ...form, smtpUsername: e.target.value })} /></Field>
            <Field label="SMTP Password"><Input type="password" value={form.smtpPassword} onChange={(e) => setForm({ ...form, smtpPassword: e.target.value })} placeholder={editId ? "•••••••••••• (leave blank to keep)" : ""} /></Field>
            <Field label="Encryption">
              <Select value={form.smtpEncryption} onChange={(e) => setForm({ ...form, smtpEncryption: e.target.value })}>
                <option value="starttls">STARTTLS</option>
                <option value="ssl_tls">SSL/TLS</option>
                <option value="none">None</option>
              </Select>
            </Field>
            <Field label="Daily Sending Limit"><Input type="number" value={form.dailySendingLimit} onChange={(e) => setForm({ ...form, dailySendingLimit: Number(e.target.value) })} /></Field>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.monitoringEnabled} onChange={(e) => setForm({ ...form, monitoringEnabled: e.target.checked })} className="h-4 w-4 rounded text-brand-600" />
              Enable automated monitoring
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.status === "active"} onChange={(e) => setForm({ ...form, status: e.target.checked ? "active" : "inactive" })} className="h-4 w-4 rounded text-brand-600" />
              Active
            </label>
          </div>
          <div className="rounded-lg border border-ink-200 p-4">
            <h4 className="mb-3 text-xs font-semibold uppercase text-ink-500">Microsoft OAuth (XOAUTH2)</h4>
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.oauthEnabled} onChange={(e) => setForm({ ...form, oauthEnabled: e.target.checked })} className="h-4 w-4 rounded text-brand-600" />
                Use Microsoft OAuth instead of an SMTP password
              </label>
              {form.oauthEnabled && (
                <>
                  <Select value={form.oauthClientId} onChange={(e) => setForm({ ...form, oauthClientId: e.target.value })} className="max-w-[220px]">
                    <option value="">Select OAuth client…</option>
                    {oauthClients?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                  {editId && form.oauthClientId ? (
                    <Button variant="secondary" size="sm" onClick={() => authorize.mutate(editId)} disabled={authorize.isPending}>
                      {authorize.isPending ? "Redirecting…" : "Authorize with Microsoft"}
                    </Button>
                  ) : (
                    <span className="text-xs text-ink-400">Save the account first, then open it again to authorize.</span>
                  )}
                </>
              )}
            </div>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={() => save.mutate(form)} disabled={save.isPending}>{editId ? "Save changes" : "Add account"}</Button>
          </div>
        </div>
      </Modal>

      <Modal open={modal === "import"} onClose={() => setModal(null)} title="Import Sending Accounts (CSV)">
        <div className="space-y-4">
          <p className="text-sm text-ink-500">Columns: name, email, domain, smtp_host, smtp_port, smtp_username, smtp_password, smtp_encryption, provider, group, daily_sending_limit, status, monitoring_enabled, notes.</p>
          <input ref={fileRef} type="file" accept=".csv" className="block w-full text-sm" onChange={async (e) => { const f = e.target.files?.[0]; if (f) await importCsv(f); }} />
          {result && (
            <div className="rounded-lg border border-ink-200 bg-ink-50 p-4 text-sm">
              Processed <strong>{result.processed}</strong>: <span className="text-emerald-600">{result.succeeded} succeeded</span>, <span className="text-red-600">{result.failed} failed</span>
              {result.errors?.length > 0 && <ul className="mt-2 max-h-40 overflow-auto space-y-1 text-xs">{result.errors.map((e: any, i: number) => <li key={i}>Row {e.row}: {e.message}</li>)}</ul>}
            </div>
          )}
          <div className="flex justify-end"><Button variant="secondary" onClick={() => setModal(null)}>Close</Button></div>
        </div>
      </Modal>
    </div>
  );
}
